import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import PageHeader from "../../../app/layout/PageHeader";
import { api } from "../../../api/apiClient";
import { notify } from "../../components-ui/toast";
import "../../../css/buttons.css";
import "../../../css/cards.css";
import "../../../css/forms.css";
import "./DuplicatePartiesPage.css";

export interface DuplicatePartySummary {
  partyId: number;
  displayName: string;
  partyType: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string | null;
  gender: string | null;
  primaryAddress: string | null;
  hasPrimaryAddress: boolean;
  emails: string[];
  mobiles: string[];
}

export interface DuplicatePartyGroup {
  parties: DuplicatePartySummary[];
  matchReasons: string[];
}

interface LegacyDuplicatePartyCandidate {
  first: DuplicatePartySummary;
  second: DuplicatePartySummary;
  matchReasons: string[];
}

interface DuplicateResolutionPreview {
  partyIds: number[];
  retainedPartyId: number;
  duplicatePartyIds: number[];
  blockingConflicts: string[];
  contactsPreserved: number;
  addressesPreserved: number;
  enrollmentsPreserved: number;
  registrationsPreserved: number;
}

interface ResolvedDuplicateParty {
  duplicatePartyId: number;
  duplicateDisplayName: string;
  retainedPartyId: number;
  retainedDisplayName: string;
  resolvedDate: string | null;
}

const groupKey = (group: DuplicatePartyGroup) =>
  group.parties.map((party) => party.partyId).sort((a, b) => a - b).join("-");

const normalizeGroups = (
  results: Array<DuplicatePartyGroup | LegacyDuplicatePartyCandidate>,
): DuplicatePartyGroup[] => results.flatMap((result) => {
  if ("parties" in result && Array.isArray(result.parties)) {
    return result.parties.length >= 2 ? [result] : [];
  }
  if ("first" in result && "second" in result && result.first && result.second) {
    return [{
      parties: [result.first, result.second],
      matchReasons: result.matchReasons ?? [],
    }];
  }
  return [];
});

export default function DuplicatePartiesPage() {
  const history = useHistory();
  const location = useLocation();
  const serviceId = Number(new URLSearchParams(location.search).get("serviceId")) || null;
  const candidatesEndpoint = serviceId
    ? `/api/duplicate-parties?serviceId=${serviceId}`
    : "/api/duplicate-parties";
  const resolvedEndpoint = serviceId
    ? `/api/duplicate-parties/resolved?serviceId=${serviceId}`
    : "/api/duplicate-parties/resolved";
  const [groups, setGroups] = useState<DuplicatePartyGroup[]>([]);
  const [resolvedDuplicates, setResolvedDuplicates] = useState<ResolvedDuplicateParty[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [activeGroupKey, setActiveGroupKey] = useState<string | null>(null);
  const [retainedPartyId, setRetainedPartyId] = useState<number | null>(null);
  const [preview, setPreview] = useState<DuplicateResolutionPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(false);
  const [confirmationName, setConfirmationName] = useState("");
  const [resolving, setResolving] = useState(false);
  const [ignoringPartyId, setIgnoringPartyId] = useState<number | null>(null);
  const [restoringPartyId, setRestoringPartyId] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api<Array<DuplicatePartyGroup | LegacyDuplicatePartyCandidate>>(candidatesEndpoint, { suppressLoading: true, notifyError: false }),
      api<ResolvedDuplicateParty[]>(resolvedEndpoint, { suppressLoading: true, notifyError: false }),
    ])
      .then(([results, resolved]) => {
        if (!cancelled) {
          setGroups(normalizeGroups(results));
          setResolvedDuplicates(resolved);
        }
      })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [candidatesEndpoint, resolvedEndpoint]);

  const activeGroup = groups.find((group) => groupKey(group) === activeGroupKey);
  const retained = activeGroup?.parties.find((party) => party.partyId === retainedPartyId);
  const duplicates = activeGroup?.parties.filter((party) => party.partyId !== retainedPartyId) ?? [];
  const previewMatchesSelection = Boolean(preview && activeGroup && retained &&
    preview.retainedPartyId === retained.partyId &&
    preview.partyIds.length === activeGroup.parties.length &&
    activeGroup.parties.every((party) => preview.partyIds.includes(party.partyId)));

  useEffect(() => {
    if (!activeGroup || !retained) return;
    let cancelled = false;
    setPreview(null);
    setPreviewLoading(true);
    setPreviewError(false);
    api<DuplicateResolutionPreview>("/api/duplicate-parties/group-resolution-preview", {
      method: "POST",
      body: {
        partyIds: activeGroup.parties.map((party) => party.partyId),
        retainedPartyId: retained.partyId,
        confirmationName: "",
      },
      suppressLoading: true,
      notifyError: false,
    })
      .then((result) => { if (!cancelled) setPreview(result); })
      .catch(() => { if (!cancelled) setPreviewError(true); })
      .finally(() => { if (!cancelled) setPreviewLoading(false); });
    return () => { cancelled = true; };
  }, [activeGroup, retained]);

  const openParty = (party: DuplicatePartySummary) => {
    history.push(`/party/${party.partyId}`, {
      partyType: party.partyType,
      returnTo: `${location.pathname}${location.search}`,
    });
  };

  const startReview = (group: DuplicatePartyGroup) => {
    setActiveGroupKey(groupKey(group));
    setRetainedPartyId(group.parties[0]?.partyId ?? null);
    setConfirmationName("");
  };

  const selectRetained = (partyId: number) => {
    setRetainedPartyId(partyId);
    setConfirmationName("");
  };

  const resolveDuplicate = async () => {
    if (!activeGroup || !retained || duplicates.length === 0 || !preview || !previewMatchesSelection || preview.blockingConflicts.length > 0 || resolving) return;
    try {
      setResolving(true);
      await api<number>("/api/duplicate-parties/resolve-group", {
        method: "POST",
        body: {
          partyIds: activeGroup.parties.map((party) => party.partyId),
          retainedPartyId: retained.partyId,
          confirmationName,
        },
      });
      setGroups((current) => current.filter((group) => groupKey(group) !== groupKey(activeGroup)));
      setActiveGroupKey(null);
      setPreview(null);
      setResolvedDuplicates((current) => [
        ...duplicates.map((duplicate) => ({
          duplicatePartyId: duplicate.partyId,
          duplicateDisplayName: duplicate.displayName,
          retainedPartyId: retained.partyId,
          retainedDisplayName: retained.displayName,
          resolvedDate: new Date().toISOString(),
        })),
        ...current,
      ]);
      notify.success(`${duplicates.length} duplicate ${duplicates.length === 1 ? "record was" : "records were"} linked to ${retained.displayName}.`);
    } catch (resolveError) {
      console.error("Could not resolve duplicate group", resolveError);
      notify.error("The duplicate group could not be resolved. Review the safety checks and try again.");
    } finally {
      setResolving(false);
    }
  };

  const ignoreCandidate = async (party: DuplicatePartySummary) => {
    if (!activeGroup || ignoringPartyId !== null ||
      !window.confirm(`${party.displayName} will no longer be suggested as a duplicate of people with this name for this organisation. Continue?`)) return;
    try {
      setIgnoringPartyId(party.partyId);
      await api<number>("/api/duplicate-parties/ignore", {
        method: "POST",
        body: { partyId: party.partyId },
      });
      const results = await api<Array<DuplicatePartyGroup | LegacyDuplicatePartyCandidate>>(
        candidatesEndpoint,
        { suppressLoading: true },
      );
      const updatedGroups = normalizeGroups(results);
      const previousIds = activeGroup.parties
        .filter((item) => item.partyId !== party.partyId)
        .map((item) => item.partyId);
      const updatedGroup = updatedGroups.find((group) =>
        group.parties.some((item) => previousIds.includes(item.partyId)));
      setGroups(updatedGroups);
      setPreview(null);
      if (updatedGroup) {
        setActiveGroupKey(groupKey(updatedGroup));
        setRetainedPartyId((current) => updatedGroup.parties.some((item) => item.partyId === current)
          ? current
          : updatedGroup.parties[0].partyId);
      } else {
        setActiveGroupKey(null);
        setRetainedPartyId(null);
      }
      setConfirmationName("");
      notify.success(`${party.displayName} was removed from this duplicate suggestion.`);
    } catch (ignoreError) {
      console.error("Could not ignore duplicate suggestion", ignoreError);
      notify.error("The duplicate suggestion could not be ignored.");
    } finally {
      setIgnoringPartyId(null);
    }
  };

  const restoreDuplicate = async (item: ResolvedDuplicateParty) => {
    if (!window.confirm(`Restore ${item.duplicateDisplayName} as an active person?`)) return;
    try {
      setRestoringPartyId(item.duplicatePartyId);
      await api<number>(`/api/duplicate-parties/${item.duplicatePartyId}/restore`, { method: "POST" });
      setResolvedDuplicates((current) => current.filter((resolved) => resolved.duplicatePartyId !== item.duplicatePartyId));
      notify.success(`${item.duplicateDisplayName} was restored.`);
    } catch (restoreError) {
      console.error("Could not restore duplicate", restoreError);
      notify.error("The person could not be restored.");
    } finally {
      setRestoringPartyId(null);
    }
  };

  return (
    <div className="page-container duplicate-parties-page">
      <PageHeader title="Potential duplicate people" subtitle="Review likely matches and choose which record to keep." />
      {loading && <p>Checking party records...</p>}
      {error && <p role="alert">Potential duplicates could not be loaded.</p>}
      {!loading && !error && groups.length === 0 && (
        <section className="card"><p>No potential duplicates found among people linked to this organisation.</p></section>
      )}
      {!loading && !error && groups.map((group) => {
        const currentGroupKey = groupKey(group);
        const isActive = activeGroupKey === currentGroupKey;
        return (
          <section className="card duplicate-party-card" key={currentGroupKey}>
            <div className="duplicate-party-group-heading">
              <div>
                <strong>{group.parties[0].displayName}</strong>
                <span>{group.parties.length} potential matching records</span>
              </div>
              <button className="action-button secondary compact" type="button" onClick={() => isActive ? setActiveGroupKey(null) : startReview(group)}>
                {isActive ? "Close" : "Review group"}
              </button>
            </div>
            <div className={`duplicate-party-workflow${isActive ? " is-active" : ""}`}>
              <div className="duplicate-party-selection">
                {isActive && (
                  <div className="duplicate-party-step-heading">
                    <span>1</span>
                    <div><strong>Select the record to keep</strong><small>The other records will be marked as duplicates.</small></div>
                  </div>
                )}
                <div className="duplicate-party-list">
                  {group.parties.map((party) => (
                    <div className={`duplicate-party-row${isActive ? " is-selecting" : ""}${isActive && retainedPartyId === party.partyId ? " is-retained" : ""}`} key={party.partyId}>
                      {isActive && (
                        <input
                          aria-label={`Keep ${party.displayName}, party ${party.partyId}`}
                          checked={retainedPartyId === party.partyId}
                          name={`retained-${currentGroupKey}`}
                          onChange={() => selectRetained(party.partyId)}
                          type="radio"
                        />
                      )}
                      <div className="duplicate-party-detail">
                        <strong>{party.displayName}</strong>
                        <span>Party #{party.partyId}{party.dateOfBirth ? ` · Born ${party.dateOfBirth.slice(0, 10)}` : ""}</span>
                        {party.emails.slice(0, 1).map((email) => <span key={`email-${party.partyId}-${email}`}>{email}</span>)}
                        {party.mobiles.slice(0, 1).map((mobile) => <span key={`mobile-${party.partyId}-${mobile}`}>{mobile}</span>)}
                      </div>
                      {isActive && (
                        <span className={`duplicate-party-outcome${retainedPartyId === party.partyId ? " keep" : " duplicate"}`}>
                          {retainedPartyId === party.partyId ? "Keep" : "Duplicate"}
                        </span>
                      )}
                      <div className="duplicate-party-row-actions">
                        <button className="action-button secondary compact" type="button" onClick={() => openParty(party)}>View</button>
                        {isActive && (
                          <button className="link-button duplicate-party-ignore" type="button" disabled={ignoringPartyId !== null} onClick={() => ignoreCandidate(party)}>
                            {ignoringPartyId === party.partyId ? "Ignoring..." : "Not a duplicate"}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <p className="duplicate-party-reasons">Matched on: {group.matchReasons.join(", ")}</p>
              </div>
              {isActive && retained && duplicates.length > 0 && (
                <div className="duplicate-party-merge-review">
                  <div className="duplicate-party-step-heading">
                    <span>2</span>
                    <div><strong>Confirm resolution</strong><small>Keep {retained.displayName} (#{retained.partyId})</small></div>
                  </div>
                  <p><strong>{duplicates.length} {duplicates.length === 1 ? "record" : "records"}</strong> will be hidden from searches. Their information and history will remain available.</p>
                  {previewLoading && <p>Checking linked records...</p>}
                  {previewError && <p role="alert">The duplicate check could not be loaded.</p>}
                  {previewMatchesSelection && preview && (
                    <>
                      {preview.blockingConflicts.length > 0 && (
                        <div className="duplicate-party-blockers" role="alert">
                          <strong>This group cannot be resolved:</strong>
                          <ul>{preview.blockingConflicts.map((conflict) => <li key={conflict}>{conflict}</li>)}</ul>
                        </div>
                      )}
                    </>
                  )}
                  <div className="form-group duplicate-party-confirmation">
                    <label className="form-label" htmlFor={`merge-confirm-${currentGroupKey}`}>Type <strong>{retained.displayName}</strong> to confirm</label>
                    <input id={`merge-confirm-${currentGroupKey}`} className="form-input" value={confirmationName} onChange={(event) => setConfirmationName(event.target.value)} />
                  </div>
                  <button className="action-button compact" type="button" disabled={resolving || !previewMatchesSelection || !preview || preview.blockingConflicts.length > 0 || confirmationName.trim().toLowerCase() !== retained.displayName.trim().toLowerCase()} onClick={resolveDuplicate}>
                    {resolving ? "Resolving..." : `Keep selected and resolve ${duplicates.length}`}
                  </button>
                </div>
              )}
            </div>
          </section>
        );
      })}
      {!loading && !error && resolvedDuplicates.length > 0 && (
        <section className="card duplicate-party-resolved">
          <div className="card-title">Resolved duplicates</div>
          <p className="field-hint">These records are hidden from normal searches. Restore one if it was resolved incorrectly.</p>
          {resolvedDuplicates.map((item) => (
            <div className="duplicate-party-resolved-row" key={item.duplicatePartyId}>
              <span><strong>{item.duplicateDisplayName}</strong> marked as a duplicate of {item.retainedDisplayName}</span>
              <div className="duplicate-party-resolved-actions">
                <button className="action-button secondary compact" type="button" onClick={() => history.push(`/party/${item.duplicatePartyId}`, { partyType: "Person", returnTo: "/admin/duplicate-parties" })}>Review record</button>
                <button className="action-button secondary compact" type="button" disabled={restoringPartyId === item.duplicatePartyId} onClick={() => restoreDuplicate(item)}>
                  {restoringPartyId === item.duplicatePartyId ? "Restoring..." : "Undo"}
                </button>
              </div>
            </div>
          ))}
        </section>
      )}
    </div>
  );
}
